import { ReactNode } from "react";
import { numar, t, useT } from "../i18n";
import type { Cheie } from "../i18n";
import { CatalogMarimi, MarimeCatalog } from "./tipuri";
import Info from "./Info";

// Explicația unei MĂRIMI (p_conv, p_ef, K_s, B, D_f …), luată din catalogul
// de pe server (`catalog_marimi.py`).
//
// De ce nu direct un <Info> scris pe loc, ca până acum: aceeași mărime apare
// în mai multe carduri și în mai mulți pași. Scrisă de mână de fiecare dată,
// ajungea explicată diferit — p_conv avea două texte în Pasul 1 și un al
// treilea în Pasul 6, tasarea și K_s câte două. Aici e una singură, deci
// stilul e același oriunde apare cifra, iar corectura se face într-un loc.
//
// Folosire:
//   <Marime cheie="p_conv" />
//   <Marime cheie="p_ef" valori={{ G: 10963, L: 162.8, B: 0.8, p_ef: 84.2 }} />
//   <EtichetaNatura cheie="p_ef" />        — «VALOARE APLICATĂ» / «LIMITĂ»,
//                                             pe card, fără hover

import { useCatalogMarimi } from "./useCatalog";
export { useCatalogMarimi };

export function marimeDin(
  catalog: CatalogMarimi | null,
  cheie: string
): MarimeCatalog | null {
  return catalog?.marimi?.[cheie] ?? null;
}

// ---------------------------------------------------------------------------
// Natura cifrei — se vede FĂRĂ hover
// ---------------------------------------------------------------------------
// „E cifra asta cât apasă, sau cât duce terenul?" era întrebarea pe care o
// punea toată lumea în fața cardurilor Pasului 6, unde stau una lângă alta
// «Presiune pe teren 84,2 kPa» și «Presiune maximă suportată 311,8 kPa»
//. Nu se rezolvă cu un tooltip: cine nu știe ce citește
// nu știe nici că are ce întreba. Deci scrie pe card.

export const NATURA: Record<string, { text: Cheie; titlu: Cheie }> = {
  valoare: { text: "struct.natura.valoare", titlu: "struct.natura.valoare_titlu" },
  limita: { text: "struct.natura.limita", titlu: "struct.natura.limita_titlu" },
  capacitate: {
    text: "struct.natura.capacitate", titlu: "struct.natura.capacitate_titlu",
  },
  geometrie: {
    text: "struct.natura.geometrie", titlu: "struct.natura.geometrie_titlu",
  },
  intrare: { text: "struct.natura.intrare", titlu: "struct.natura.intrare_titlu" },
  derivat: { text: "struct.natura.derivat", titlu: "struct.natura.derivat_titlu" },
};

export function EtichetaNaturaText({ natura }: { natura?: string }) {
  const t = useT();
  const n = natura ? NATURA[natura] : null;
  if (!n) return null;
  return (
    <span className={`mr-natura mr-natura-${natura}`} title={t(n.titlu)}>
      {t(n.text)}
    </span>
  );
}

/** Eticheta de natură a unei mărimi din catalog, pusă direct pe card. */
export function EtichetaNatura({ cheie }: { cheie: string }) {
  const m = marimeDin(useCatalogMarimi(), cheie);
  if (!m?.natura) return null;
  return <EtichetaNaturaText natura={m.natura} />;
}

// ---------------------------------------------------------------------------
// Formula cu valorile proiectului substituite
// ---------------------------------------------------------------------------
// Regula casei: o formulă fără cifrele proiectului nu explică nimic — cine
// citește „p_ef = G/(L·B)" nu află de unde vine 84,2. Catalogul scrie formula
// cu placeholdere `{G}`, `{L}`, `{B}`, `{p_ef}`, iar pagina dă valorile. Un
// placeholder pentru care pagina n-a dat nimic NU dispare: rămâne vizibil ca
// simbol, marcat, ca lipsa să se vadă și să poată fi reparată.

export type ValoriFormula = Record<string, number | string | null | undefined>;

const fmt = (v: number | string) =>
  typeof v === "number" ? numar(v, { maximumFractionDigits: 3 }) : v;

export function formulaCuValori(
  formula: string,
  valori?: ValoriFormula
): ReactNode[] {
  const bucati: ReactNode[] = [];
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  let ultim = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(formula)) !== null) {
    if (m.index > ultim) bucati.push(formula.slice(ultim, m.index));
    const v = valori?.[m[1]];
    if (v == null || v === "")
      bucati.push(
        <span key={`l${i++}`} className="mr-lipsa" title={t("struct.marime.lipsa")}>
          {m[1]}
        </span>
      );
    else
      bucati.push(
        <strong key={`v${i++}`} className="mr-val">
          {fmt(v)}
        </strong>
      );
    ultim = m.index + m[0].length;
  }
  if (ultim < formula.length) bucati.push(formula.slice(ultim));
  return bucati;
}

/** Formula unei mărimi, cu valorile proiectului, ca text în pagină. */
export function FormulaMarime({
  cheie,
  valori,
}: {
  cheie: string;
  valori?: ValoriFormula;
}) {
  const m = marimeDin(useCatalogMarimi(), cheie);
  if (!m?.formula) return null;
  return <span className="mr-formula">{formulaCuValori(m.formula, valori)}</span>;
}

export default function Marime({
  cheie,
  valori,
  extra,
}: {
  cheie: string;
  /** valorile reale ale proiectului pentru placeholderele din formulă */
  valori?: ValoriFormula;
  /** Adaos specific pasului curent (valori concrete ale proiectului), pus
   *  după explicația comună — ca particularul să nu rescrie generalul. */
  extra?: ReactNode;
}) {
  const catalog = useCatalogMarimi();
  const m = marimeDin(catalog, cheie);
  // cât timp catalogul nu a sosit, iconița nu se randează deloc: mai bine
  // lipsă decât un cartonaș gol care arată a eroare
  if (!m) return null;
  return (
    <Info
      titlu={m.eticheta}
      natura={
        m.natura ? (
          <span className="fx-tip-block">
            <span className="fx-tip-label">{t("struct.simbol.ce_fel")}</span>
            <span>
              <EtichetaNaturaText natura={m.natura} />{" "}
              <span className="cv-de-unde">
                {NATURA[m.natura] ? t(NATURA[m.natura].titlu) : ""}
              </span>
            </span>
          </span>
        ) : null
      }
      ce={m.ce}
      deCe={
        <>
          {m.de_ce}
          {m.formula && (
            <>
              <br />
              <br />
              <strong>{t("struct.marime.formula_cu_valori")}</strong>{" "}
              <span className="cv-formula mr-formula-tip">
                {formulaCuValori(m.formula, valori)}
              </span>
            </>
          )}
          {m.cum_obtinut && (
            <>
              <br />
              <strong>{t("struct.simbol.cum_obtinut")}</strong> {m.cum_obtinut}
            </>
          )}
          {m.articol && (
            <>
              <br />
              <strong>{t("struct.simbol.articol")}</strong> {m.articol}
            </>
          )}
          {extra && (
            <>
              <br />
              <br />
              {extra}
            </>
          )}
        </>
      }
      parametri={m.parametri}
    />
  );
}
